/**
 * AddNewCleanDialog — the ONE shared admin creation form ("Add New Clean").
 * Used from the dashboard, the schedule/calendar and every client profile.
 * Persists through the canonical POST /api/admin/appointments/quick-create.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Search, CalendarPlus, CheckCircle2, Plus, X } from 'lucide-react';
import MultiGuruSelect from './MultiGuruSelect';
import { AddressEditDialog } from '../../pages/admin/AdminClientsPage';

const API_URL = process.env.REACT_APP_BACKEND_URL;
export const BUSINESS_TZ = 'America/Los_Angeles';
const pad2 = (n) => String(n).padStart(2, '0');

// ISO (any zone) → { date: 'YYYY-MM-DD', time: 'HH:mm' } in the business timezone
export const isoToBusinessParts = (iso) => {
  if (!iso) return { date: '', time: '' };
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone: BUSINESS_TZ, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
    .formatToParts(new Date(iso)).map((p) => [p.type, p.value]));
  return { date: `${parts.year}-${parts.month}-${parts.day}`, time: `${parts.hour === '24' ? '00' : parts.hour}:${parts.minute}` };
};

// Business-timezone wall clock → UTC ISO
export const businessPartsToIso = (date, time) => {
  const guess = new Date(`${date}T${time}:00Z`);
  const asTz = new Date(guess.toLocaleString('en-US', { timeZone: BUSINESS_TZ }));
  return new Date(guess.getTime() + (guess.getTime() - asTz.getTime())).toISOString();
};

const fmtBusiness = (iso) => new Date(iso).toLocaleString('en-US', { timeZone: BUSINESS_TZ, weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
const newRequestId = () => window.crypto?.randomUUID?.() || `clean-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const addrLabel = (a) => [a.line1 || a.street || a.address_line1, a.city].filter(Boolean).join(', ') || a.label || 'Property';

const AddNewCleanDialog = ({ open, onOpenChange, headers, defaultStart, defaultEnd, defaultClient, onCreated, fromQuote }) => {
  const navigate = useNavigate();
  const [clientQuery, setClientQuery] = useState('');
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [addressId, setAddressId] = useState('');
  const [addressDialog, setAddressDialog] = useState(false);
  const [services, setServices] = useState([]);
  const [serviceId, setServiceId] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [duration, setDuration] = useState(120);
  const [price, setPrice] = useState('');
  const [instructions, setInstructions] = useState('');
  const [notes, setNotes] = useState('');
  const [team, setTeam] = useState([]);
  const [errors, setErrors] = useState({});
  const [conflicts, setConflicts] = useState(null);
  const [blockReason, setBlockReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const requestId = useRef(newRequestId());
  const searchTimer = useRef(null);

  const defaultClientRef = useRef(defaultClient);
  defaultClientRef.current = defaultClient;
  const defaultClientKey = defaultClient?.user_id || defaultClient?.id || null;

  // Seed on open (keyed on client id so parent re-renders never wipe entries)
  useEffect(() => {
    if (!open) return;
    const defaultClient = defaultClientRef.current;
    const s = defaultStart ? isoToBusinessParts(defaultStart) : { date: '', time: '' };
    setDate(s.date); setTime(s.time || (s.date ? '09:00' : ''));
    if (defaultStart && defaultEnd) setDuration(Math.max(15, Math.round((new Date(defaultEnd) - new Date(defaultStart)) / 60000)));
    setSelectedClient(defaultClient || null); setClientQuery(''); setClients([]);
    setAddresses([]); setAddressId(''); setTeam([]); setInstructions(''); setNotes('');
    setPrice(fromQuote ? (fromQuote.versions[fromQuote.accepted_version - 1].amount_cents / 100).toFixed(2) : ''); setErrors({}); setConflicts(null); setCreated(null);
    if (fromQuote) setInstructions(fromQuote.versions[fromQuote.accepted_version - 1].scope);
    requestId.current = newRequestId();
  }, [open, defaultStart, defaultEnd, defaultClientKey, fromQuote]);

  useEffect(() => {
    if (!open || services.length) return;
    axios.get(`${API_URL}/api/services`, { headers }).then((r) => setServices(r.data || [])).catch(() => setServices([]));
  }, [open, services.length, headers]);

  const service = useMemo(() => services.find((s) => s.id === serviceId), [services, serviceId]);
  useEffect(() => {
    if (!service) return;
    setDuration(service.default_estimated_duration_minutes || service.duration_minutes || 120);
  }, [service]);

  const loadAddresses = useCallback(async (uid, preferId) => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/crm/clients/${uid}/full`, { headers });
      const list = r.data?.addresses || [];
      setAddresses(list);
      const primary = fromQuote ? list.find((a) => a.id === fromQuote.address_id) : list.find((a) => a.id === preferId) || list.find((a) => a.is_primary || a.is_default) || (list.length === 1 ? list[0] : null);
      setAddressId(primary ? primary.id : '');
    } catch { setAddresses([]); setAddressId(''); }
  }, [headers, fromQuote]);

  useEffect(() => {
    const uid = selectedClient?.user_id || selectedClient?.id;
    if (open && uid) loadAddresses(uid);
  }, [open, selectedClient, loadAddresses]);

  const searchClients = useCallback((q) => {
    if (!q || q.length < 2) { setClients([]); return; }
    axios.get(`${API_URL}/api/admin/customers?search=${encodeURIComponent(q)}`, { headers })
      .then((r) => setClients((r.data?.customers || []).slice(0, 8))).catch(() => setClients([]));
  }, [headers]);
  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => searchClients(clientQuery), 250);
    return () => clearTimeout(searchTimer.current);
  }, [clientQuery, searchClients]);

  const clientUid = selectedClient?.user_id || selectedClient?.id;

  const validate = () => {
    const e = {};
    if (!clientUid) e.client = 'Select a client';
    if (clientUid && !addressId) e.address = addresses.length ? 'Select the property' : 'This client has no property yet — add one';
    if (!serviceId) e.service = 'Select a service';
    if (!date) e.date = 'Pick a date';
    if (!time) e.time = 'Pick a start time';
    if (!(Number(duration) >= 15)) e.duration = 'Duration must be at least 15 minutes';
    if (String(price).trim() === '') e.price = 'Enter the agreed price for this clean';
    else if (!/^\d+(\.\d{1,2})?$/.test(String(price).trim())) e.price = 'Enter a valid price of $0 or more (up to 2 decimals)';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const save = async (override = false) => {
    if (!validate()) return;
    const needsReason = conflicts?.errors?.some((c) => c.requires_reason);
    if (override && needsReason && !blockReason.trim()) { toast.error('Enter a reason to book over the blocked time.'); return; }
    setSaving(true); setConflicts(null);
    const startIso = businessPartsToIso(date, time);
    const endIso = new Date(new Date(startIso).getTime() + Number(duration) * 60000).toISOString();
    try {
      const res = await axios.post(`${API_URL}/api/admin/appointments/quick-create`, {
        client_user_id: clientUid, address_id: addressId, service_id: serviceId,
        start_time: startIso, end_time: endIso, assigned_cleaner_user_ids: team,
        notes_client: instructions || null, notes_internal: notes || null,
        quoted_price: Number(String(price).trim()),
        client_request_id: requestId.current, admin_override: override, quote_id: fromQuote?.id || null,
        block_override_reason: override ? (blockReason.trim() || null) : null,
      }, { headers });
      setCreated(res.data);
      onCreated?.(res.data);
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 409 && d?.errors) setConflicts(d);
      else toast.error(typeof d === 'string' ? d : 'Could not save the clean — your entries are kept, please try again.');
    } finally { setSaving(false); }
  };

  const reset = () => { requestId.current = newRequestId(); setCreated(null); setConflicts(null); setBlockReason(''); setDate(''); setTime(''); setInstructions(''); setNotes(''); setTeam([]); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1.5rem)] sm:max-w-2xl max-h-[92vh] overflow-y-auto p-4 sm:p-6" data-testid="add-new-clean-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" data-testid="add-new-clean-title"><CalendarPlus className="h-5 w-5 text-[#F56F05]" /> Add New Clean</DialogTitle>
          <DialogDescription>Times are in Pacific (business) time. The clean is saved as Booked; a Guru can be assigned now or later. No customer messages or charges are sent.</DialogDescription>
        </DialogHeader>

        {created ? (
          <div className="space-y-4 py-2" data-testid="add-new-clean-success">
            <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <CheckCircle2 className="h-6 w-6 text-emerald-600 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-bold text-emerald-900">Clean added — {fmtBusiness(created.start_time)}</p>
                <p className="text-emerald-800 mt-1">{created.client_name || selectedClient?.user_name || selectedClient?.full_name} · {created.service_name || service?.name}{team.length ? '' : ' · Unassigned'}</p>
                <p className="text-emerald-700/80 text-xs mt-2">It now appears in the Backlog, on the schedule for that date, and in the client's Upcoming cleans. If your current view is filtered to another date, status or Guru, use Open Clean below.</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <Button variant="outline" onClick={reset} data-testid="add-another-clean-btn">Add another</Button>
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="close-add-new-clean-btn">Close</Button>
              <Button className="bg-[#F56F05] hover:bg-[#C2410C] text-white" onClick={() => { onOpenChange(false); navigate(`/admin/appointments/${created.id}`); }} data-testid="open-clean-btn">Open Clean</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-1">
            {/* Client */}
            <div>
              <Label className="text-xs">Client</Label>
              {selectedClient ? (
                <div className="flex items-center justify-between rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm" data-testid="selected-client">
                  <span className="truncate"><b>{selectedClient.user_name || selectedClient.full_name || selectedClient.name}</b> <span className="text-zinc-500">{selectedClient.user_email || selectedClient.email}</span></span>
                  {!fromQuote && <button type="button" className="text-zinc-400 hover:text-zinc-700" onClick={() => { setSelectedClient(null); setAddresses([]); setAddressId(''); }} aria-label="Change client" data-testid="change-client-btn"><X className="h-4 w-4" /></button>}
                </div>
              ) : (
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-zinc-400 pointer-events-none" />
                  <Input className="pl-8" placeholder="Search clients by name, email or phone" value={clientQuery} onChange={(e) => setClientQuery(e.target.value)} data-testid="clean-client-search" />
                  {clients.length > 0 && (
                    <div className="absolute z-20 mt-1 w-full rounded-md border border-zinc-200 bg-white shadow-lg max-h-56 overflow-y-auto" data-testid="clean-client-results">
                      {clients.map((c) => (
                        <button key={c.user_id || c.id || c.email} type="button" disabled={!(c.user_id || c.id)} onClick={() => { setSelectedClient(c); setClients([]); setClientQuery(''); }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-orange-50 disabled:opacity-50" data-testid={`clean-client-option-${c.user_id || c.id}`}>
                          <span className="font-medium">{c.user_name || c.full_name}</span> <span className="text-zinc-500 text-xs">{c.user_email || c.email}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {errors.client && <p className="text-xs text-red-600 mt-1" data-testid="error-client">{errors.client}</p>}
            </div>

            {/* Property */}
            {clientUid && (
              <div>
                <Label className="text-xs">Property / service address</Label>
                <div className="flex gap-2">
                  <Select value={addressId} onValueChange={setAddressId} disabled={!addresses.length || !!fromQuote}>
                    <SelectTrigger data-testid="clean-address-select"><SelectValue placeholder={addresses.length ? 'Select property' : 'No property on file'} /></SelectTrigger>
                    <SelectContent>{addresses.map((a) => <SelectItem key={a.id} value={a.id}>{addrLabel(a)}{a.is_primary ? ' (primary)' : ''}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button type="button" variant="outline" size="sm" className="h-10" onClick={() => setAddressDialog(true)} data-testid="clean-add-property-btn"><Plus className="h-4 w-4 mr-1" /> Add property</Button>
                </div>
                {errors.address && <p className="text-xs text-red-600 mt-1" data-testid="error-address">{errors.address}</p>}
              </div>
            )}

            {/* Service + price */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <Label className="text-xs">Service</Label>
                <Select value={serviceId} onValueChange={setServiceId}>
                  <SelectTrigger data-testid="clean-service-select"><SelectValue placeholder="Select service" /></SelectTrigger>
                  <SelectContent>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
                {errors.service && <p className="text-xs text-red-600 mt-1" data-testid="error-service">{errors.service}</p>}
              </div>
              <div>
                <Label className="text-xs">Price ($)</Label>
                <Input type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="Enter price" data-testid="clean-price" />
                {errors.price && <p className="text-xs text-red-600 mt-1" data-testid="error-price">{errors.price}</p>}
              </div>
            </div>

            {/* Schedule */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div><Label className="text-xs">Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="clean-date" />{errors.date && <p className="text-xs text-red-600 mt-1" data-testid="error-date">{errors.date}</p>}</div>
              <div><Label className="text-xs">Start time (PT)</Label><Input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="clean-time" />{errors.time && <p className="text-xs text-red-600 mt-1" data-testid="error-time">{errors.time}</p>}</div>
              <div className="col-span-2 sm:col-span-1"><Label className="text-xs">Duration (minutes)</Label><Input type="number" min="15" step="15" value={duration} onChange={(e) => setDuration(e.target.value)} data-testid="clean-duration" />{errors.duration && <p className="text-xs text-red-600 mt-1">{errors.duration}</p>}</div>
            </div>

            {/* Guru */}
            <div>
              <Label className="text-xs">Guru(s) — optional, leave empty to save as Unassigned</Label>
              <MultiGuruSelect value={team} onChange={setTeam} headers={headers} testIdPrefix="clean-team" placeholder="Assign later or pick now…" />
            </div>

            {/* Notes */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">Cleaning instructions (client-visible)</Label><Textarea rows={3} value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="Entry details, focus areas, pets…" data-testid="clean-instructions" /></div>
              <div><Label className="text-xs">Internal notes (staff only)</Label><Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything the office should know" data-testid="clean-internal-notes" /></div>
            </div>

            {conflicts && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="clean-conflicts">
                <p className="font-semibold text-amber-900">Scheduling check</p>
                <ul className="list-disc pl-5 mt-1 text-amber-900">{conflicts.errors.map((c, i) => <li key={i}>{c.message}{c.conflicting_appointment_id && <> — <button type="button" className="underline" onClick={() => navigate(`/admin/appointments/${c.conflicting_appointment_id}`)}>view conflicting clean</button></>}</li>)}</ul>
                {conflicts.errors.some((c) => c.requires_reason) && conflicts.overridable && (
                  <div className="mt-2"><Label className="text-xs text-amber-900">Reason for booking over blocked time</Label>
                    <Input value={blockReason} onChange={(e) => setBlockReason(e.target.value)} maxLength={300} data-testid="clean-block-override-reason" /></div>
                )}
                <p className="text-xs text-amber-800 mt-2">{conflicts.overridable ? 'These rules can be overridden by an admin.' : 'Guru overlaps cannot be overridden — change the time or the Guru.'}</p>
              </div>
            )}

            <div className="flex flex-wrap justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
              {conflicts?.overridable && <Button variant="outline" className="border-amber-400 text-amber-900" onClick={() => save(true)} disabled={saving} data-testid="save-clean-override-btn">Override and save</Button>}
              <Button className="bg-[#F56F05] hover:bg-[#C2410C] text-white" onClick={() => save(false)} disabled={saving} data-testid="save-clean-btn">
                {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</> : 'Save Clean'}
              </Button>
            </div>
          </div>
        )}

        {addressDialog && clientUid && (
          <AddressEditDialog mode="add" userId={clientUid} headers={headers} onClose={() => setAddressDialog(false)}
            onSaved={() => { setAddressDialog(false); loadAddresses(clientUid); toast.success('Property added'); }} />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddNewCleanDialog;
